import AppForms from "@toothfairy/shared-ui/AppForms";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../../Constants";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import { isLargeScreen } from "../../../MainViewContainer";
import RelationshipPanel from "./RelationshipPanel";
import {
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const AUTH_SECTION_FIELD_MAP = {
  profile: ["name", "description", "type"],
  credentials: [
    "tokenSecret",
    "desk_username",
    "desk_password",
    "basic_username",
    "basic_password",
  ],
  oauth: [
    "clientId",
    "clientSecret",
    "authorizationBaseUrl",
    "tokenBaseUrl",
    "grantType",
    "scope",
    "staticArgs",
  ],
};

const AuthorisationSettingsAIView = ({
  isEdit,
  settingType,
  handleDeletion,
  isSaveInProgress,
  onUpdateSecret,
  updatingSecrets,
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const { currentTheme, mode } = AppTheme.useTheme();
  const entityName = t("settings.authorisation.entityName");
  const { setFieldValue, values, initialValues, errors, submitCount } =
    AppForms.useFormikContext();
  const { state } = AppStateManager.useAppStateManager();

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: AUTH_SECTION_FIELD_MAP,
  });

  const isOauth = values?.type === "oauth";
  const isUserPass = values?.type === "username_and_password";
  const isBasic = values?.type === "basic";
  const hasGenericSecret =
    values?.type !== "none" && !isOauth && !isUserPass && !isBasic;

  const _title = useCallback(() => {
    return isEdit ? t("common.save") : t("common.create");
  }, [entityName, isEdit]);

  const handleUpdateSecretClick = useCallback(
    (fieldName) => {
      if (fieldName === "desk_credentials") {
        setFieldValue("desk_username", "");
        setFieldValue("desk_password", "");
      } else if (fieldName === "basic_credentials") {
        setFieldValue("basic_username", "");
        setFieldValue("basic_password", "");
      } else {
        setFieldValue(fieldName, "");
      }
      onUpdateSecret(fieldName);
    },
    [setFieldValue, onUpdateSecret]
  );

  return (
    <>
      {_showBuilderSection("profile") && (
        <SectionRenderer
          title={t("settings.authorisation.builderSectionProfile", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          <View>
            {isEdit && (
              <IdCopyView
                id={values?.id}
                label={t("settings.authorisation.id")}
              />
            )}
            <AppForms.AppFormField
              showTextInputLabel
              isMandatory
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              label={t("settings.authorisation.secretName")}
              placeholder={t("settings.authorisation.secretName")}
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="name"
            />
            <AppForms.AppFormField
              showTextInputLabel
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              multiline
              textContainer={{
                minHeight: 120,
                paddingVertical: 10,
                marginHorizontal: 5,
              }}
              label={t("common.description")}
              placeholder={
                values?.type == "username_and_password"
                  ? t("settings.authorisation.descriptionPlaceholderDesktop")
                  : t("settings.authorisation.descriptionPlaceholder")
              }
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="description"
            />
            <AppForms.AppFormDropDown
              isElevated={false}
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
                maxWidth: !isLargeScreen() && 250,
              }}
              inputLabelStyle={{
                marginLeft: 5,
              }}
              disabled={isEdit}
              showTextInputLabel
              label={t("common.type")}
              isMandatory
              name="type"
              items={[
                {
                  id: "apikey",
                  name: t("settings.authorisation.types.apiKey"),
                },
                {
                  id: "bearer",
                  name: t("settings.authorisation.types.bearer"),
                },
                {
                  id: "oauth",
                  name: t("settings.authorisation.types.oauth2"),
                },
                {
                  id: "password",
                  name: t("settings.authorisation.types.password"),
                },
                {
                  id: "env",
                  name: t("settings.authorisation.types.environmentVariable"),
                },
                {
                  id: "username_and_password",
                  name: t("settings.authorisation.types.usernameAndPassword"),
                },
                {
                  id: "generic",
                  name: t("settings.authorisation.types.genericSecret"),
                },
                {
                  id: "basic",
                  name: t("settings.authorisation.types.basicAuth"),
                },
              ]}
              propertyLabel="name"
              propertyValue="id"
            />
          </View>
        </SectionRenderer>
      )}

      {_showBuilderSection("credentials") &&
        (hasGenericSecret || isUserPass || isBasic) && (
          <SectionRenderer
            title={t("settings.authorisation.builderSectionCredentials", {
              defaultValue: "Credentials",
            })}
            icon={AppAssets.icons.credentials}
          >
            {hasGenericSecret && (
              <View style={{ position: "relative" }}>
                <AppForms.AppFormField
                  showTextInputLabel
                  isMandatory
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  label={
                    ["password", "env", "generic"]?.includes(values?.type)
                      ? t("settings.authorisation.secret")
                      : t("settings.authorisation.token")
                  }
                  placeholder={
                    isEdit && !updatingSecrets.has("tokenSecret")
                      ? t("settings.authorisation.secretCannotBeViewed")
                      : isEdit && updatingSecrets.has("tokenSecret")
                      ? t("settings.authorisation.enterNewSecret")
                      : ["password", "env", "generic"]?.includes(values?.type)
                      ? t("settings.authorisation.secretPlaceholder")
                      : t("settings.authorisation.tokenPlaceholder")
                  }
                  disabled={isEdit && !updatingSecrets.has("tokenSecret")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name={
                    isEdit && !updatingSecrets.has("tokenSecret")
                      ? "$xc"
                      : "tokenSecret"
                  }
                />
                {isEdit && !updatingSecrets.has("tokenSecret") && (
                  <ToothFairyButton
                    title={t("settings.authorisation.updateSecret")}
                    onPress={() => handleUpdateSecretClick("tokenSecret")}
                    style={{
                      height: 25,
                      paddingHorizontal: 0,
                    }}
                    btnContentStyle={{
                      paddingHorizontal: 5,
                    }}
                    backgroundColor={currentTheme?.theme?.soft_primary}
                    wrapperStyle={{
                      position: "absolute",
                      right: 0,
                      top: 49,
                      minWidth: 120,
                      height: 25,
                      zIndex: 10,
                    }}
                  />
                )}
              </View>
            )}

            {isUserPass && (
              <View style={{ position: "relative" }}>
                <AppForms.AppFormField
                  showTextInputLabel
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  isMandatory
                  label={t("settings.authorisation.username")}
                  placeholder={
                    isEdit && !updatingSecrets.has("desk_credentials")
                      ? t("settings.authorisation.usernameEmailCannotBeViewed")
                      : isEdit && updatingSecrets.has("desk_credentials")
                      ? t("settings.authorisation.enterNewUsername")
                      : t("settings.authorisation.usernameEmailPlaceholder")
                  }
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name="desk_username"
                  disabled={isEdit && !updatingSecrets.has("desk_credentials")}
                />
                <AppForms.AppFormField
                  showTextInputLabel
                  isMandatory
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  label={t("settings.authorisation.password")}
                  placeholder={
                    isEdit && !updatingSecrets.has("desk_credentials")
                      ? t("settings.authorisation.passwordsCannotBeViewed")
                      : isEdit && updatingSecrets.has("desk_credentials")
                      ? t("settings.authorisation.enterNewPassword")
                      : t("settings.authorisation.passwordPlaceholder")
                  }
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name={
                    isEdit && !updatingSecrets.has("desk_credentials")
                      ? "$xc"
                      : "desk_password"
                  }
                  disabled={isEdit && !updatingSecrets.has("desk_credentials")}
                />
                {isEdit && !updatingSecrets.has("desk_credentials") && (
                  <ToothFairyButton
                    title={t("settings.authorisation.updateCredentials")}
                    onPress={() => handleUpdateSecretClick("desk_credentials")}
                    style={{
                      height: 25,
                      paddingHorizontal: 0,
                    }}
                    btnContentStyle={{
                      paddingHorizontal: 5,
                    }}
                    backgroundColor={currentTheme?.theme?.soft_primary}
                    wrapperStyle={{
                      position: "absolute",
                      right: 0,
                      top: 35,
                      minWidth: 140,
                      height: 25,
                      zIndex: 10,
                    }}
                  />
                )}
              </View>
            )}

            {isBasic && (
              <View style={{ position: "relative" }}>
                <AppForms.AppFormField
                  showTextInputLabel
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  isMandatory
                  label={t("settings.authorisation.username")}
                  placeholder={
                    isEdit && !updatingSecrets.has("basic_credentials")
                      ? t("settings.authorisation.usernameCannotBeViewed")
                      : isEdit && updatingSecrets.has("basic_credentials")
                      ? t("settings.authorisation.enterNewUsername")
                      : t("settings.authorisation.basicAuthUsernamePlaceholder")
                  }
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name="basic_username"
                  disabled={isEdit && !updatingSecrets.has("basic_credentials")}
                />
                <AppForms.AppFormField
                  showTextInputLabel
                  isMandatory
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  label={t("settings.authorisation.password")}
                  placeholder={
                    isEdit && !updatingSecrets.has("basic_credentials")
                      ? t("settings.authorisation.passwordCannotBeViewed")
                      : isEdit && updatingSecrets.has("basic_credentials")
                      ? t("settings.authorisation.enterNewPassword")
                      : t("settings.authorisation.basicAuthPasswordPlaceholder")
                  }
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name={
                    isEdit && !updatingSecrets.has("basic_credentials")
                      ? "$xc"
                      : "basic_password"
                  }
                  disabled={isEdit && !updatingSecrets.has("basic_credentials")}
                />
                {isEdit && !updatingSecrets.has("basic_credentials") && (
                  <ToothFairyButton
                    title={t("settings.authorisation.updateCredentials")}
                    onPress={() => handleUpdateSecretClick("basic_credentials")}
                    style={{
                      height: 25,
                      paddingHorizontal: 0,
                    }}
                    btnContentStyle={{
                      paddingHorizontal: 5,
                    }}
                    backgroundColor={currentTheme?.theme?.soft_primary}
                    wrapperStyle={{
                      position: "absolute",
                      right: 0,
                      top: 35,
                      minWidth: 140,
                      height: 25,
                      zIndex: 10,
                    }}
                  />
                )}
              </View>
            )}
          </SectionRenderer>
        )}

      {_showBuilderSection("oauth") && isOauth && (
        <SectionRenderer
          title={t("settings.authorisation.builderSectionOauth", {
            defaultValue: "OAuth",
          })}
          icon={AppAssets.icons.lock}
        >
          <>
            <View
              style={{
                zIndex: -1,
              }}
            >
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                isMandatory
                label={t("settings.authorisation.clientId")}
                placeholder={t("settings.authorisation.clientId")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="clientId"
              />
            </View>

            <View style={{ position: "relative" }}>
              <AppForms.AppFormField
                showTextInputLabel
                isMandatory
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                label={t("settings.authorisation.clientSecret")}
                placeholder={
                  isEdit && !updatingSecrets.has("clientSecret")
                    ? t("settings.authorisation.secretsCannotBeViewed")
                    : isEdit && updatingSecrets.has("clientSecret")
                    ? t("settings.authorisation.enterNewClientSecret")
                    : t("settings.authorisation.clientSecretPlaceholder")
                }
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name={
                  isEdit && !updatingSecrets.has("clientSecret")
                    ? "$xc"
                    : "clientSecret"
                }
                disabled={isEdit && !updatingSecrets.has("clientSecret")}
              />
              {isEdit && !updatingSecrets.has("clientSecret") && (
                <ToothFairyButton
                  title={t("settings.authorisation.updateSecret")}
                  onPress={() => handleUpdateSecretClick("clientSecret")}
                  style={{
                    height: 25,
                    paddingHorizontal: 0,
                  }}
                  btnContentStyle={{
                    paddingHorizontal: 5,
                  }}
                  backgroundColor={currentTheme?.theme?.soft_primary}
                  wrapperStyle={{
                    position: "absolute",
                    right: 0,
                    top: 49,
                    minWidth: 120,
                    height: 25,
                    zIndex: 10,
                  }}
                />
              )}
            </View>
            <View style={{ position: "relative" }}>
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                label={t("settings.authorisation.refreshToken")}
                placeholder={
                  isEdit && !updatingSecrets.has("tokenSecret")
                    ? t("settings.authorisation.secretsCannotBeViewed")
                    : isEdit && updatingSecrets.has("tokenSecret")
                    ? t("settings.authorisation.enterNewRefreshToken")
                    : t("settings.authorisation.refreshTokenPlaceholder")
                }
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name={
                  isEdit && !updatingSecrets.has("tokenSecret")
                    ? "$xc_token"
                    : "tokenSecret"
                }
                disabled={isEdit && !updatingSecrets.has("tokenSecret")}
              />
              {isEdit && !updatingSecrets.has("tokenSecret") && (
                <ToothFairyButton
                  title={t("settings.authorisation.updateSecret")}
                  onPress={() => handleUpdateSecretClick("tokenSecret")}
                  style={{
                    height: 25,
                    paddingHorizontal: 0,
                  }}
                  btnContentStyle={{
                    paddingHorizontal: 5,
                  }}
                  backgroundColor={currentTheme?.theme?.soft_primary}
                  wrapperStyle={{
                    position: "absolute",
                    right: 0,
                    top: 49,
                    minWidth: 120,
                    height: 25,
                    zIndex: 10,
                  }}
                />
              )}
            </View>
            <View
              style={{
                zIndex: -1,
              }}
            >
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                isMandatory
                label={t("settings.authorisation.authorisationUrl")}
                placeholder={t("settings.authorisation.authorisationUrl")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="authorizationBaseUrl"
              />
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                label={t("settings.authorisation.tokenUrl")}
                placeholder={t("settings.authorisation.tokenUrlPlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="tokenBaseUrl"
              />
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                isMandatory
                label={t("settings.authorisation.grantType")}
                placeholder={t("settings.authorisation.grantTypePlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="grantType"
              />
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                label={t("settings.authorisation.scope")}
                placeholder={t("settings.authorisation.scope")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="scope"
              />
              <AppForms.AppFormFieldJSONV2
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                mode="code"
                formFieldContainerStyle={{
                  zIndex: -1,
                }}
                textInputLabelStyle={{
                  marginLeft: 10,
                }}
                darkMode={mode === "dark"}
                label={t("settings.authorisation.staticArguments")}
                placeholder={t(
                  "settings.authorisation.staticArgumentsPlaceholder"
                )}
                name="staticArgs"
              />
            </View>
          </>
        </SectionRenderer>
      )}

      {_showBuilderSection("usage") && isEdit && values?.id && (
        <SectionRenderer
          title={t("settings.authorisation.builderSectionUsage", {
            defaultValue: "Usage",
          })}
          icon={AppAssets.icons.taskAgent}
        >
          <RelationshipPanel
            isEdit={isEdit}
            dependsOn={[]}
            usedBy={[
              ...(state?.agentsFunctions
                ?.filter((f) => f?.authorisationID === values?.id)
                ?.map((f) => ({
                  id: f.id,
                  name: f.name,
                  type: "tool",
                })) || []),
              ...(state?.execEnvs
                ?.filter((e) => {
                  let rawSecrets = e?.customExecutionSecrets;
                  if (typeof rawSecrets === "string") {
                    try {
                      rawSecrets = JSON.parse(rawSecrets);
                    } catch {
                      rawSecrets = [];
                    }
                  }
                  const secretIds = Array.isArray(rawSecrets)
                    ? rawSecrets.map((s) => (typeof s === "string" ? s : s?.id))
                    : [];
                  return secretIds.includes(values?.id);
                })
                ?.map((e) => ({
                  id: e.id,
                  name: e.name,
                  type: "hook",
                })) || []),
            ]}
          />
        </SectionRenderer>
      )}

      {!isBuilderLayout && (
        <View style={{ alignItems: "center" }}>
          <AppForms.AppSubmitButton
            title={_title()}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            isDisabled={isSaveInProgress}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            // btnLabelStyle={{ fontSize: 16 }}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />
          {isEdit && (
            <ToothFairyButton
              title={t("common.delete")}
              onPress={handleDeletion}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}
    </>
  );
};

export default AuthorisationSettingsAIView;
